// App.js
import React from 'react';
import { BrowserRouter as Router, Route, Routes, Outlet } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box } from '@mui/material';
import { theme, GlobalStyles } from './styles';
import AuthProvider from './context/AuthContext';
import { PublicRoute, UserRoute, ClientRoute, AdminRoute, CoachRoute, SubscribedCoachRoute } from './routes/ProtectedRoutes';

// Layouts
import AdminLayout from './components/layout/AdminLayout';
import CoachLayout from './components/layout/CoachLayout';
import ClientLayout from './components/layout/ClientLayout';
import TopBar from './components/TopBar';
import Footer from './components/Footer';

// Auth
import Login from './views/account/Login';
import AdminLogin from './views/account/AdminLogin';
import Register from './views/account/Register';
import ForgotPassword from './views/account/ForgotPassword';
import ResetPassword from './views/account/ResetPassword';
import EditProfil from './views/account/EditProfil';

// Admin (nouveau)
import AdminDashboard from './views/admin/AdminDashboard';
import AdminProfile from './views/admin/AdminProfile';
import CoachListAdmin from './views/admin/coaches/CoachList';
import CoachDetail from './views/admin/coaches/CoachDetail';
import CoachForm from './views/admin/coaches/CoachForm';
import TenantList from './views/admin/tenants/TenantList';
import WaitingList from './views/admin/waitinglist/WaitingList';
import Documentation from './views/admin/Documentation';

// Recipes (partagé)
import RecipeForm from './views/recipes/CreateRecipe';
import RecipeList from './views/recipes/RecipeList';
import EditRecipe from './views/recipes/EditRecipe';

// Legacy Admin (à migrer progressivement)
import Dashboard from './views/Dashboard';
import UserList from './views/users/UserList';
import UserView from './views/users/UserView';
import EditUser from './views/users/EditUser';
import AddUser from './views/users/AddUser';
import CreateWeekPlan from './views/plans/CreateWeekPlan';
import PlanList from './views/plans/PlanList';
import PlanView from './views/plans/PlanView';
import EditPlan from './views/plans/EditPlan';

// User
import UserPlanView from './views/plans/UserPlanView';

// Client Portal
import ClientDashboard from './views/client/ClientDashboard';
import ClientMeals from './views/client/ClientMeals';
import ClientShopping from './views/client/ClientShopping';
import ClientProfile from './views/client/ClientProfile';

// Static
import PrivacyPolicy from './views/PrivacyPolicy';

// Coach
import CoachDashboard from './views/coach/CoachDashboard';
import CheckoutSuccessPage from './views/coach/checkout/CheckoutSuccessPage';

import AddClient from './views/coach/clients/AddClient';
import ClientList from './views/coach/clients/ClientList';
import ClientView from './views/coach/clients/ClientView';
import EditClient from './views/coach/clients/EditClient';

import CoachPlanList from './views/coach/plans/CoachPlanList';
import CoachCreateWeekPlan from './views/coach/plans/CoachCreateWeekPlan.js';
import CoachPlanView from './views/coach/plans/CoachPlanView.js';
import CoachEditPlan from './views/coach/plans/CoachEditPlan.js';

// Layout wrapper avec TopBar et Footer (pour pages legacy)
const PublicLayout = () => (
  <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
    <TopBar />
    <Box sx={{ flex: 1 }}>
      <Outlet />
    </Box>
    <Footer />
  </Box>
);

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <GlobalStyles />
      <HelmetProvider>
        <Router>
          <AuthProvider>
            <Routes>

              {/* ==================== LOGIN PAGES (SANS LAYOUT) ==================== */}
              <Route element={<PublicRoute />}>
                <Route path="/" element={<Login />} />
                <Route path="/admin-login" element={<AdminLogin />} />
                <Route path="/register" element={<Register />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password/:token" element={<ResetPassword />} />
              </Route>

              {/* ==================== ROUTES ADMIN (NOUVEAU LAYOUT SIDEBAR) ==================== */}
              <Route element={<AdminRoute />}>
                <Route element={<AdminLayout />}>
                  <Route path="/admin" element={<AdminDashboard />} />
                  <Route path="/admin/coaches" element={<CoachListAdmin />} />
                  <Route path="/admin/coaches/new" element={<CoachForm />} />
                  <Route path="/admin/coaches/:id" element={<CoachDetail />} />
                  <Route path="/admin/coaches/:id/edit" element={<CoachForm />} />
                  <Route path="/admin/tenants" element={<TenantList />} />
                  <Route path="/admin/waiting-list" element={<WaitingList />} />
                  <Route path="/admin/recipes" element={<RecipeList />} />
                  <Route path="/admin/recipes/create" element={<RecipeForm />} />
                  <Route path="/admin/recipes/edit/:id" element={<EditRecipe />} />
                  <Route path="/admin/documentation" element={<Documentation />} />
                  <Route path="/admin/profile" element={<AdminProfile />} />
                </Route>
              </Route>

              {/* ==================== ROUTES COACH (NOUVEAU LAYOUT SIDEBAR) ==================== */}
              <Route element={<SubscribedCoachRoute />}>
                <Route element={<CoachLayout />}>
                  <Route path="/coach" element={<CoachDashboard />} />
                  <Route path="/coach/clients" element={<ClientList />} />
                  <Route path="/coach/clients/create" element={<AddClient />} />
                  <Route path="/coach/clients/view/:id" element={<ClientView />} />
                  <Route path="/coach/clients/edit/:id" element={<EditClient />} />
                  <Route path="/coach/plans" element={<CoachPlanList />} />
                  <Route path="/coach/plans/create" element={<CoachCreateWeekPlan />} />
                  <Route path="/coach/plans/view/:clientId/:weekPlanId" element={<CoachPlanView />} />
                  <Route path="/coach/plans/edit/:clientId/:weekPlanId" element={<CoachEditPlan />} />
                  <Route path="/coach/recipes" element={<RecipeList />} />
                  <Route path="/coach/recipes/create" element={<RecipeForm />} />
                  <Route path="/coach/recipes/edit/:id" element={<EditRecipe />} />
                  <Route path="/coach/profile" element={<EditProfil />} />
                </Route>
              </Route>

              {/* ==================== ROUTE CHECKOUT SUCCESS (SANS LAYOUT) ==================== */}
              <Route element={<CoachRoute />}>
                <Route path="/coach/checkout/success" element={<CheckoutSuccessPage />} />
              </Route>

              {/* ==================== ROUTES CLIENT (MOBILE-FIRST LAYOUT) ==================== */}
              <Route element={<ClientRoute />}>
                <Route element={<ClientLayout />}>
                  <Route path="/client" element={<ClientDashboard />} />
                  <Route path="/client/meals" element={<ClientMeals />} />
                  <Route path="/client/shopping" element={<ClientShopping />} />
                  <Route path="/client/profile" element={<ClientProfile />} />
                </Route>
              </Route>

              {/* ==================== ROUTES AVEC LAYOUT CLASSIQUE ==================== */}
              <Route element={<PublicLayout />}>

                {/* Pages publiques accessibles à tous */}
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />

                {/* Routes User */}
                <Route element={<UserRoute />}>
                  <Route path="/week" element={<UserPlanView />} />
                  <Route path="/profile" element={<EditProfil />} />
                </Route>

                {/* Routes Admin Legacy (rétrocompatibilité) */}
                <Route element={<AdminRoute />}>
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/recipes" element={<RecipeList />} />
                  <Route path="/recipes/create" element={<RecipeForm />} />
                  <Route path="/recipes/edit/:id" element={<EditRecipe />} />
                  <Route path="/users" element={<UserList />} />
                  <Route path="/users/create" element={<AddUser />} />
                  <Route path="/users/view/:id" element={<UserView />} />
                  <Route path="/users/edit/:id" element={<EditUser />} />
                  <Route path="/plans" element={<PlanList />} />
                  <Route path="/plans/create" element={<CreateWeekPlan />} />
                  <Route path="/plans/view/:id" element={<PlanView />} />
                  <Route path="/plans/edit/:id" element={<EditPlan />} />
                </Route>

              </Route>

            </Routes>
          </AuthProvider>
        </Router>
      </HelmetProvider>
    </ThemeProvider>
  );
}

export default App;
